import { useGetUser } from '@web/api/users/use-get-user';
import { useIsAuthenticated } from '@web/redux/hooks';
import { LDContextCommon, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';

export function useInitializeLaunchDarklyIdentifier() {
  const isAuthenticated = useIsAuthenticated();
  const { data: userData } = useGetUser({ enabled: isAuthenticated });
  const ldClient = useLDClient();

  // Keep track of the last identity we sent to LaunchDarkly to avoid re-identifying
  // due to unrelated state changes (e.g., loading flags flipping during refetches).
  const lastIdentityRef = useRef<string | null>(null);

  // By using ldClient above, the app does not re-render when the user is updated (login, logout)
  useEffect(() => {
    if (!ldClient) return;

    const userKey = isAuthenticated && userData?.id ? userData.id : undefined;
    const companyKey = userData?.launchDarklyPayload.intacctCompanyId;

    // Compute the target identity signature. When authenticated we require both keys.
    // When unauthenticated, we use a stable 'anonymous' signature.
    const identitySignature =
      userKey && companyKey ? (`auth:${userKey}:${companyKey}` as const) : !isAuthenticated ? ('anonymous' as const) : null;

    // If we don't yet have enough data to identify, bail out until we do.
    if (!identitySignature) return;

    // Skip if identity hasn't changed.
    if (lastIdentityRef.current === identitySignature) return;
    lastIdentityRef.current = identitySignature;

    if (identitySignature === 'anonymous') {
      void ldClient
        .identify({
          kind: 'multi',
          user: { key: 'ANONYMOUS_USER' } satisfies LDContextCommon,
          company: { key: 'ANONYMOUS_COMPANY' } satisfies LDContextCommon,
          anonymous: true,
        })
        .catch();
      return;
    }

    // Authenticated identity with user and company keys available
    if (!userKey || !companyKey || !userData) return;

    void ldClient
      .identify({
        kind: 'multi',
        user: {
          key: userKey,
          custom: userData.launchDarklyPayload,
        } satisfies LDContextCommon,
        company: {
          key: companyKey,
          custom: {
            companyId: userData.launchDarklyPayload.companyId,
            companyTenantType: userData.launchDarklyPayload.companyTenantType,
          },
        } satisfies LDContextCommon,
      })
      .catch();
  }, [ldClient, isAuthenticated, userData]);

  // We only want to use the LaunchDarkly Client to identify the user. The provider is created before the whole app is rendered.
}
